import type { QueryClient } from '@tanstack/react-query';
import type { Listing, ListingGrouping } from 'app/types/listing';

import { listingKeys, recommendationKeys } from './keys';

export function hasListingUser(listing: unknown): listing is Listing {
  return typeof listing === 'object' && listing !== null && 'user' in listing && Boolean((listing as Listing).user);
}

function getMergedCounter(previousValue?: number | null, nextValue?: number | null) {
  if (!Number.isInteger(previousValue) && !Number.isInteger(nextValue)) {
    return undefined;
  }

  return Math.max(previousValue ?? 0, nextValue ?? 0);
}

export function mergeListingDetails(previous: Listing | undefined, next: Listing): Listing {
  if (!previous) {
    return next;
  }

  const numViews = getMergedCounter(previous.numViews, next.numViews);
  const numInteractions = getMergedCounter(previous.numInteractions, next.numInteractions);
  // Unlike views and interactions, favourites can decrease when someone removes a favourite.
  // Always accept the latest server value instead of retaining the highest cached count.
  const numFavourites = next.numFavourites ?? previous.numFavourites;
  const trustPoints = getMergedCounter(previous.user.trustPoints, next.user.trustPoints);

  return {
    ...previous,
    ...next,
    lender: next.lender === undefined ? previous.lender : next.lender,
    lenders: next.lenders === undefined ? previous.lenders : next.lenders,
    additionalLendersCount:
      next.additionalLendersCount === undefined ? previous.additionalLendersCount : next.additionalLendersCount,
    numViews,
    numInteractions,
    numFavourites,
    user: {
      ...previous.user,
      ...next.user,
      trustPoints,
    },
  };
}

export function mergeListingDetailCache(client: QueryClient, listing: Listing) {
  client.setQueryData<Listing>(listingKeys.detail(listing.id), (previous) => {
    if (!hasListingUser(previous)) {
      return listing;
    }

    return mergeListingDetails(previous, listing);
  });
}

export function updateGroupingFavouriteCache(client: QueryClient, groupingId: string, grouping?: ListingGrouping) {
  const favourite = grouping?.groupingFavourites?.[0];
  let representative: Listing | undefined;

  const updateCachedListings = (cached: unknown): unknown => {
    if (Array.isArray(cached)) {
      const updated = cached.map(updateCachedListings);
      return updated.some((entry, index) => entry !== cached[index]) ? updated : cached;
    }
    if (!cached || typeof cached !== 'object') return cached;

    if (hasListingUser(cached) && cached.grouping?.id === groupingId) {
      const updated: Listing = {
        ...cached,
        grouping: { ...cached.grouping, groupingFavourites: favourite ? [favourite] : undefined },
      };
      if (grouping?.listings.some((listing) => listing.id === cached.id)) representative ??= updated;
      return updated;
    }

    // Listing queries contain arrays, paginated results, category/tag sections, or map entries.
    let updated = cached as Record<string, unknown>;
    for (const key of ['pages', 'results', 'listings', 'listing']) {
      if (!(key in updated)) continue;
      const value = updateCachedListings(updated[key]);
      if (value !== updated[key]) updated = { ...updated, [key]: value };
    }
    return updated;
  };

  const cachedQueries = [
    ...client.getQueriesData({ queryKey: listingKeys.all }),
    ...client.getQueriesData({ queryKey: recommendationKeys.lists() }),
  ];
  for (const [queryKey, cached] of cachedQueries) {
    const updated = updateCachedListings(cached);
    if (updated !== cached) client.setQueryData(queryKey, updated);
  }
  client.setQueryData<Listing[]>(listingKeys.list('favourites'), (previous) => {
    if (!grouping) return previous?.filter((listing) => listing.grouping?.id !== groupingId);
    if (!previous || previous.some((listing) => listing.grouping?.id === groupingId)) return previous;

    const listing = representative ?? grouping.listings[0];
    if (!listing) return previous;

    return [
      ...previous,
      {
        ...listing,
        grouping: listing.grouping ?? {
          id: groupingId,
          key: grouping.key,
          totalOffers: grouping.totalOffers ?? grouping.listings.length,
          listings: grouping.listings,
          groupingFavourites: favourite ? [favourite] : undefined,
        },
      },
    ];
  });
}
